import { useState, useEffect, useReducer } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { isEmpty } from 'lodash'
import { toaster } from '../helpers/toaster';
import { getStudy, updateStudy, syncSingleStudy } from '../api/studyListApi'

// reducer action types
// dirty fields reducer
const SET_DIRTY = 'SET_DIRTY'
const SET_CLEAN = 'SET_CLEAN'
const CLEAR = 'CLEAR'
// study
const INIT_STUDY = 'INIT_STUDY'
const ADD_ITEM = 'ADD_ITEM'
const UPDATE_ITEM = 'UPDATE_ITEM'
const DELETE_ITEM = 'DELETE_ITEM'
// submit data reducer
const SET_OVERRIDE_DATA = 'SET_OVERRIDE_DATA'
const SET_SPONSOR_DATA = 'SET_SPONSOR_DATA'
const SET_RELATED_STUDIES = 'SET_RELATED_STUDIES'
const SET_COMMERCIAL_PRODUCTS = 'SET_COMMERCIAL_PRODUCTS'
const SET_DEVELOPMENT_STATUS = 'SET_DEVELOPMENT_STATUS'

const useStudyData = (studyId) => {

  // GET CURRENT DATA
  const studyDetailsReducer = (state, action) => {
    let newState = { ...state }
    let items = []

    switch (action.type) {
      // { type, data }
      case INIT_STUDY:
        return action.data

      /**
       * Update app state for grid items.
       *  actions: { type, itemType }
       *    itemType: 'study_registries', 'attachments', 'locations'
       */
      
      // actions: { type, itemType, item }
      case ADD_ITEM:
        if (state?.[action.itemType]?.length > 0) {
          items = [...state?.[action.itemType]]
        }
        items.push(action.item)
        newState[action.itemType] = items
        return newState

      // actions: { type, itemType, item }
      // TODO: rename UPDATE_ARRAY_ITEM?
      case UPDATE_ITEM:
        if (state?.[action.itemType]?.length > 0) {
          items = state[action.itemType].map((item) => {
            if (item.id === action.item.id) {
              return action.item
            } else {
              return item
            }
          })
          newState[action.itemType] = items
          return newState
        }

      // actions: { type, itemType, id }
      case DELETE_ITEM:
        if (state?.[action.itemType]?.length > 0) {
          items = state[action.itemType].reduce((acc, cur) => {
            if (cur.id !== action.id) acc.push(cur)
            return acc
          }, [])
          newState[action.itemType] = items
        }
        return newState
    }
  }

  const [studyDetails, setStudyDetails] = useReducer(studyDetailsReducer, null)

  const getStudyData = async (id) => {
    const res = await getStudy(id)
    setStudyDetails({ type: INIT_STUDY, data: { ...res } })
  }

  /**
   * Transform values from study data for irregular fields
   * @param {String} id 
   * @param {String} dataKeyword 'override' or 'ctg_data'
   * @returns Object or Array
   */
  const getFieldValue = (id, dataKeyword) => {
    if (id) {
      if (id === 'maximum_age' || id === 'minimum_age') {
        // data in format { id, text }
        return studyDetails?.[dataKeyword]?.[id]?.text
      } else if (id === 'intervention_name') {
        // string data saved as one item in an array
        return studyDetails?.[dataKeyword]?.[id]?.[0]
      } else if (id === 'conditions') {
        // values found in data.condtions
        return studyDetails?.conditions
      }
      else if (id === 'interventions') {
        // values found in data.condtions
        return studyDetails?.interventions
      }
      return studyDetails?.[dataKeyword]?.[id]
    }
    return null;
  }

  useEffect(() => {
    if (!studyId) return
    getStudyData(studyId)
  }, [studyId])

  // SET DATA TO UPDATE

  const [fieldStatuses, setFieldStatuses] = useState(null)

  /**
   * @param {Object} state 
   * @param {Object} action:
   *   action format for SET_DIRTY and SET_CLEAN: { type, name, section }
   *     optional "section" values: "override" or "sponsor_data"
   *     don't include "section" for type "related_studies"
   *   action format for CLEAR: { type, name, sections }
   *     optional sections array, eg, ["override", "related_studies"]
   * @returns state object
   */
  const dirtyFieldsReducer = (state, action) => {
    switch (action.type) {
      case SET_DIRTY: {
        let newState = {}
        if (state) {
          newState = { ...state }
          if (action.section) {
            if (state[action.section]) {
              newState[action.section] = { ...state[action.section], [action.name]: true }
            } else {
              newState[action.section] = { [action.name]: true }
            }
          } else { // related_studies
            newState[action.name] = true
          }
        }
        return newState
      }
      case SET_CLEAN: {
        if (state) {
          let newState = { ...state }
          if (action.section) {
            if (state?.[action.section]?.[action.name]) {
              newState[action.section] = { ...state[action.section] }
              delete newState[action.section][action.name]
              if (isEmpty(newState[action.section])) {
                delete newState[action.section]
              }
            }
          } else { // related_studies
            if (state?.[action.name]) delete newState[action.name];
          }
          return newState
        } else {
          return null
        }
      }
      case CLEAR: {
        if (state && action?.sections?.length > 0) {
          const newState = { ...state }
          action.sections.forEach((sect) => {
            if (state?.[sect]) delete newState[sect]
          })
          return newState
        } else {
          return null
        }
      }
      default: {
        throw new Error(`Unhandled dirty fields action type: ${action.type}`)
      }
    }
  }

  const [dirtyFields, setDirtyFields] = useReducer(dirtyFieldsReducer, null)

  /**
   * Save data to get ready for submission.
   * @param {Object} state
   * @param {Object} action: { type, name, value }
   * @returns 
   */
  const submitDataReducer = (state, action) => {
    let newState = null
    if (state) {
      newState = { ...state }
    } else {
      newState = {}
    }
    const name = action.name
    let section = null;
    let value = action.value
    let isDirty = false

    let isFieldGroup = false
    if (
      name === 'trial_arms_group' ||
      name === 'primary_outcome_measures' ||
      name === 'secondary_outcome_measures'
      // || name === 'hyperlink_buttons'
    ) isFieldGroup = true;

    if (name === 'related_studies') {
      // TODO: refactor related_studies logic to be more similar to fields saved to study data root, eg, logo, maybe has_results...?
      newState[name] = value

      // isDirty calculations
      const valueIdArray = action?.value?.map((item) => Number(item.id))?.sort()
      const savedIdArray = studyDetails?.[name]?.map((item) => Number(item.id))?.sort()
      isDirty = (JSON.stringify(valueIdArray) !== JSON.stringify(savedIdArray));

      // if value is empty and value saved is empty array
      if (!value && studyDetails?.[name]?.length === 0) isDirty = false;

    } else if(name === 'commercial_products') {
            // TODO: refactor related_studies logic to be more similar to fields saved to study data root, eg, logo, maybe has_results...?
            newState[name] = value

            // isDirty calculations
            const valueIdArray = action?.value?.map((item) => Number(item.id))?.sort()
            const savedIdArray = studyDetails?.[name]?.map((item) => Number(item.id))?.sort()
            isDirty = (JSON.stringify(valueIdArray) !== JSON.stringify(savedIdArray));
      
            // if value is empty and value saved is empty array
            if (!value && studyDetails?.[name]?.length === 0) isDirty = false;
    } 
    else if(name === 'development_status') {
      // TODO: refactor related_studies logic to be more similar to fields saved to study data root, eg, logo, maybe has_results...?
      newState[name] = value

      // isDirty calculations
      const valueIdArray = action?.value
      const savedIdArray = studyDetails?.[name]
      isDirty = (valueIdArray !== savedIdArray);

      // if value is empty and value saved is empty array
      if (!value && studyDetails?.[name]?.length === 0) isDirty = false;
    } 
    else { // not related_studies

      /**
       * Send null vs. "" or [] if want to avoid saving property,
       *   because submitData function removes null objects.
       * If want to clear arrays, send empty []
       */
      if (action.value === '' || action.value?.length === 0){
        if (action.name !== 'logo'){
          value = null
        }
      }
      // if (name === 'hyperlink_buttons') {
      //   section = 'sponsor_data'
      // } 
      if (action.type === SET_OVERRIDE_DATA || isFieldGroup) {
        section = 'override'
      } else if (action.type === SET_SPONSOR_DATA) {
        section = 'sponsor_data'
      } else {
        // throw new Error(`Unhandled submit data action type: ${action.type}`)
      }

      if (section && !newState?.[section]) {
        if (studyDetails?.[section]) {
          newState[section] = { ...studyDetails[section] }
        } else {
          newState[section] = {}
        }
      }

      // intervention_name: one item in array
      if (action.name === 'intervention_name') {
        action.value ? value = [value] : value = null;
      }

      if (isFieldGroup) {
        let prevState = []
        if (newState[section][name]) prevState = [...newState[section][name]]

        let options = {}
        // CREATE_GROUP
        if (action.fieldMap) options.fieldMap = action.fieldMap;
        // SET_FIELD, DELETE_ITEM
        if (action.id) options.id = action.id
        // SET_FIELD
        if (action.field) options.field = action.field;
        if (action.value) options.value = action.value;
        // MOVE_UP, MOVE_DOWN
        if (action.order_id) options.order_id = action.order_id

        value = updateFieldGroup(prevState, action.type, options)
      }


      if (section) {

        newState[section][action.name] = value

        isDirty = (JSON.stringify(value) !== JSON.stringify(studyDetails?.[section]?.[name]));

        // isDirty calculations

        // if both value saved and new value are undefined or null, not dirty
        if (isFieldGroup && !value && !studyDetails?.[section]?.[name]) {
          isDirty = false
        } else if (!isFieldGroup && !action.value && !studyDetails?.[section]?.[name]) {
          // TODO: see if (!value && !studyDetails?.[section]?.[name]) works instead
          isDirty = false
        }

        // isDirty for ages based on "calculated" prop
        if (
          (name === 'maximum_age' || name === 'minimum_age') &&
          action?.value?.calculated
        ) {
          if (action.value.calculated === studyDetails?.[section]?.[name]?.calculated) isDirty = false;
        }

      } else if (name === 'logo') { // no section
        newState[action.name] = value
        setFieldStatuses({ sponsor_data: { logo: null } })
        // TODO: compare with what gets saved to determine if dirty instead of hard-coding!
        isDirty = true
      }
    }

    const dirtyType = isDirty ? SET_DIRTY : SET_CLEAN
    setDirtyFields({ type: dirtyType, section, name })

    return newState
  }

  const updateFieldGroup = (state, type, {
    // CREATE_GROUP
    fieldMap = null,
    // SET_FIELD
    field = null,
    id = null,
    value = null,
    // MOVE_UP, MOVE_DOWN, DELETE_ITEM
    order_id = null,
  }) => {
    let newState = []
    switch (type) {

      // option reqs: fieldMap
      case 'CREATE_GROUP': {
        if (state?.length > 0) newState = [...state];
        let newGroup = fieldMap.reduce((acc, field) => {
          acc[field.id] = ''
          return acc
        }, {})
        if (state?.length > 0) {
          newGroup.order_id = state.length + 1
        } else {
          newGroup.order_id = 1
        }
        newGroup.id = uuidv4()
        newState.push(newGroup)
        return newState
      }

      // option reqs: id (UUIDs), field, value
      case 'SET_FIELD': {
        if (state?.length > 0) {
          newState = state.map((item) => {
            let newItem = { ...item }
            if (item.id === id) newItem[field] = value;
            return newItem
          })
          return newState
        }
        return []
      }

      // option reqs: order_id (1, 2, 3)
      case 'MOVE_UP': {
        if (state && order_id !== 1) state.forEach((item) => {
          if (item.order_id === order_id - 1) {
            // item above: move down
            newState[order_id - 1] = { ...item }
            newState[order_id - 1].order_id = order_id
          } else if (item.order_id === order_id) {
            // item to move up
            newState[order_id - 2] = { ...item }
            newState[order_id - 2].order_id = order_id - 1
          } else {
            newState[item.order_id - 1] = { ...item }
          }
        })
        return newState
      }

      // option reqs: order_id (1, 2, 3)
      case 'MOVE_DOWN': {
        if (state && order_id !== state.length) state.forEach((item) => {
          if (item.order_id === order_id) {
            // item to move down
            newState[order_id] = { ...item }
            newState[order_id].order_id = order_id + 1
          } else if (item.order_id === order_id + 1) {
            // item below: move up
            newState[order_id - 1] = { ...item }
            newState[order_id - 1].order_id = order_id
          } else {
            newState[item.order_id - 1] = { ...item }
          }
        })
        return newState
      }

      // option reqs: id (UUIDs)
      case 'DELETE_ITEM': {
        const deleteorder_id = state.reduce((result, item) => {
          if (item.id === id) {
            result = item.order_id
          }
          return result
        }, '')
        newState = state.reduce((acc, item) => {
          if (item.id !== id) {
            let newItem = { ...item }
            if (deleteorder_id && item.order_id > deleteorder_id) {
              newItem.order_id = item.order_id - 1
            }
            acc.push(newItem)
          }
          return acc
        }, [])
        return newState
      }
    }
  }

  /**
   * setDataToSubmit: set data to submit later
   * Usage: setDataToSubmit({ type, name, value })
   *   If any props are null, remove prop when submittinv
   */
  const [dataToSubmit, setDataToSubmit] = useReducer(submitDataReducer, null)

  // temp // good for troubleshooting
  // useEffect(() => {
  //   if (!isEmpty(dataToSubmit)) console.log('dataToSubmit', dataToSubmit)
  // }, [dataToSubmit])

  // temp // good for troubleshooting
  // useEffect(() => {
  //   console.log('dirtyFields', dirtyFields)
  // }, [dataToSubmit])

  // SAVE DATA

  const showSuccessToast = () => {
    toaster("Study updated successfully", "success")
  }

  const showErrorToast = (error) => {
    
    toaster(`${error}`, "error")
  }

  /**
   * @param {Array} dataKeywords "sponsor_data", "related_studies", "logo"
   * @returns void
   */
  const submitData = async (dataKeywords = null) => {
    try {
      if (dataToSubmit) {
        let payload = new FormData()
        let _fieldStatuses = null

        /**
         * NOTE: like override and sponsor data objects,
         * related_studies is an object and logo is a file blob,
         * both of which need to be saved to the root of the study object.
         * but unlike them should not be iterated thru for props.
         */
        const addDataToPayload = (key) => {
          if (dataToSubmit[key]) {
            if (key === 'related_studies') {
              payload.append(key, JSON.stringify(dataToSubmit[key]))
            } else if (key === 'logo') {
              _fieldStatuses = { sponsor_data: { logo: 'pending' } }
              setFieldStatuses(_fieldStatuses)
              if(dataToSubmit[key] === 'remove'){
                payload.append('logo_remove', 'remove')
              }
              else{
                payload.append(key, dataToSubmit[key])
              }
              
            } else if (key === 'commercial_products') {
              payload.append(key, JSON.stringify(dataToSubmit[key]))
            } else if (key === 'development_status') {
              payload.append(key, JSON.stringify(dataToSubmit[key]))
            } else {
              let props = {}
              // iterate thru object
              // do not include null props in payload
              for (const prop in dataToSubmit[key]) {
                if ( (prop === 'conditions' || prop === 'interventions') && dataToSubmit[key][prop] === null ){
                  props[prop] = []
                }
                if (dataToSubmit[key][prop] !== null) {
                  props[prop] = dataToSubmit[key][prop]
                }
              }
              payload.append(key, JSON.stringify(props))
            }
          }
        }

        if (dataKeywords?.length > 0) {
          // save one section at a time
          dataKeywords.forEach((keyword) => {
            // dataKeywords.map((keyword) => {
            console.log(keyword);
            addDataToPayload(keyword)
          })
        } else {
          // save all sections
          for (const key in dataToSubmit) {
            addDataToPayload(key)
          }
        }

        const res = await updateStudy(studyDetails.id, payload)
        if (res.data) {
          setStudyDetails({ type: INIT_STUDY, data: res.data })
          setDirtyFields({ type: CLEAR, sections: dataKeywords })
          showSuccessToast()

          // success status
          if (_fieldStatuses?.sponsor_data?.logo === 'pending') {
            setFieldStatuses({ sponsor_data: { logo: 'resolved' } })
          }
        }
      } else {
        console.log(`no data to submit`)
      }
    } catch (err) {
      console.log('submitData ERROR:', err)
    }
  }

  const submitManualData = async () => {
    submitData(['sponsor_data', 'related_studies', 'logo', 'commercial_products', 'development_status'])
  }

  const submitConneXData = async () => {
    submitData(['override'])
  }

  const submitBooleanField = async (id, value) => {
    let payload = new FormData()
    payload.append(id, value)
    const res = await updateStudy(studyDetails.id, payload)
    if (res.status === 200) {
      showSuccessToast()
    } else {
      showErrorToast(res.statusText)
    }
  }

  const showSyncSuccessToast = () => {
    toaster("Study synced successfully", "success")
  }

  const syncStudy = async () => {
    try {
      const res = await syncSingleStudy(studyDetails.id);
      if (res.status === 200) {
        showSyncSuccessToast();
      }
      else {
        showErrorToast(res.statusText);
      }
      setStudyDetails({ type: INIT_STUDY, data: res.data })
    }
    catch (e) {
      showErrorToast(`Could not sync study: ${e.response.data?.Message}`);
    }
  }

  return {
    // GET CURRENT DATA
    INIT_STUDY,
    studyDetails,
    setStudyDetails,
    getFieldValue,

    // SET DATA TO UPDATE
    dirtyFields,
    dataToSubmit,
    setDataToSubmit,
    SET_OVERRIDE_DATA,
    SET_SPONSOR_DATA,
    SET_RELATED_STUDIES,
    SET_COMMERCIAL_PRODUCTS,
    SET_DEVELOPMENT_STATUS,

    // SAVE DATA
    submitData,
    submitConneXData,
    submitManualData,
    submitBooleanField,
    fieldStatuses,

    //SYNC DATA
    syncStudy,
  }
}

export default useStudyData